import { API_ENDPOINTS } from '../config/api';
import { apiService } from './api';

export interface ReportTemplate {
  id: string;
  name: string;
  description: string;
  type: 'standard' | 'executive' | 'technical' | 'legal' | 'insurance' | 'custom';
  sections: ReportSection[];
  layout: ReportLayout;
  branding: ReportBranding;
  created_at: string;
  updated_at: string;
  created_by: string;
}

export interface ReportSection {
  id: string;
  title: string;
  type: 'text' | 'table' | 'image' | 'chart' | 'timeline' | 'map' | 'summary';
  content: any;
  order: number;
  visible: boolean;
  mandatory: boolean;
  options?: {
    columns?: string[];
    format?: string;
    style?: string;
  };
}

export interface ReportLayout {
  format: 'A4' | 'Letter' | 'Legal';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  header: {
    enabled: boolean;
    height: number;
    content: string;
  };
  footer: {
    enabled: boolean;
    height: number;
    content: string;
  };
  watermark?: {
    enabled: boolean;
    text: string;
    opacity: number;
  };
}

export interface ReportBranding {
  logo?: string;
  company_name: string;
  company_address?: string;
  company_phone?: string;
  company_email?: string;
  company_website?: string;
  colors: {
    primary: string;
    secondary: string;
    accent: string;
  };
  fonts: {
    primary: string;
    secondary: string;
  };
}

export interface ReportRequest {
  enquete_id: string;
  template_id: string;
  title: string;
  subtitle?: string;
  recipient?: {
    name: string;
    email: string;
    company?: string;
  };
  custom_sections?: ReportSection[];
  delivery_method: 'download' | 'email' | 'both';
  confidentiality_level: 'public' | 'internal' | 'confidential' | 'secret';
  expires_in_days?: number;
}

export interface ReportData {
  id: string;
  enquete_id: string;
  template_id: string;
  title: string;
  subtitle?: string;
  generated_at: string;
  generated_by: string;
  status: 'draft' | 'generated' | 'sent' | 'archived';
  recipient?: {
    name: string;
    email: string;
    company?: string;
  };
  metadata: {
    total_pages: number;
    file_size: number;
    generation_time: number;
    version: number;
  };
  file_path?: string;
  shared_link?: string;
  expires_at?: string;
  download_url?: string;
}

export interface ReportAnalytics {
  total_reports: number;
  reports_by_type: Record<string, number>;
  reports_by_status: Record<string, number>;
  average_generation_time: number;
  most_used_templates: string[];
  reports_by_month: Record<string, number>;
}

class ReportAPI {
  // Templates
  async getTemplates(): Promise<ReportTemplate[]> {
    // Fallback pour les templates si l'API n'est pas disponible
    const fallbackTemplates: ReportTemplate[] = [
      {
        id: 'standard',
        name: 'Rapport Standard',
        description: 'Rapport d\'enquête standard avec toutes les sections de base',
        type: 'standard',
        sections: [
          { id: '1', title: 'Résumé exécutif', type: 'text', content: '', order: 1, visible: true, mandatory: true },
          { id: '2', title: 'Contexte de l\'enquête', type: 'text', content: '', order: 2, visible: true, mandatory: true },
          { id: '3', title: 'Méthodologie', type: 'text', content: '', order: 3, visible: true, mandatory: false },
          { id: '4', title: 'Résultats OSINT', type: 'table', content: '', order: 4, visible: true, mandatory: true },
          { id: '5', title: 'Personnes impliquées', type: 'table', content: '', order: 5, visible: true, mandatory: false },
          { id: '6', title: 'Lieux d\'intérêt', type: 'map', content: '', order: 6, visible: true, mandatory: false },
          { id: '7', title: 'Chronologie', type: 'timeline', content: '', order: 7, visible: true, mandatory: false },
          { id: '8', title: 'Conclusions', type: 'text', content: '', order: 8, visible: true, mandatory: true },
          { id: '9', title: 'Recommandations', type: 'text', content: '', order: 9, visible: true, mandatory: false },
          { id: '10', title: 'Annexes', type: 'text', content: '', order: 10, visible: true, mandatory: false }
        ],
        layout: {
          format: 'A4',
          orientation: 'portrait',
          margins: { top: 20, right: 20, bottom: 20, left: 20 },
          header: { enabled: true, height: 50, content: 'RAPPORT D\'ENQUÊTE OSINT' },
          footer: { enabled: true, height: 30, content: 'Page {page} sur {total}' }
        },
        branding: {
          company_name: 'NetMeSafe',
          colors: { primary: '#FF5722', secondary: '#2D3748', accent: '#4299E1' },
          fonts: { primary: 'Arial', secondary: 'Helvetica' }
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'system'
      },
      {
        id: 'executive',
        name: 'Rapport Exécutif',
        description: 'Rapport condensé pour les dirigeants',
        type: 'executive',
        sections: [
          { id: '1', title: 'Résumé exécutif', type: 'text', content: '', order: 1, visible: true, mandatory: true },
          { id: '2', title: 'Points clés', type: 'summary', content: '', order: 2, visible: true, mandatory: true },
          { id: '3', title: 'Risques identifiés', type: 'table', content: '', order: 3, visible: true, mandatory: true },
          { id: '4', title: 'Recommandations urgentes', type: 'text', content: '', order: 4, visible: true, mandatory: true }
        ],
        layout: {
          format: 'A4',
          orientation: 'portrait',
          margins: { top: 20, right: 20, bottom: 20, left: 20 },
          header: { enabled: true, height: 50, content: 'RAPPORT EXÉCUTIF' },
          footer: { enabled: true, height: 30, content: 'Confidentiel - Page {page}' }
        },
        branding: {
          company_name: 'NetMeSafe',
          colors: { primary: '#FF5722', secondary: '#2D3748', accent: '#E53E3E' },
          fonts: { primary: 'Arial', secondary: 'Helvetica' }
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'system'
      },
      {
        id: 'technical',
        name: 'Rapport Technique',
        description: 'Rapport détaillé avec aspects techniques',
        type: 'technical',
        sections: [
          { id: '1', title: 'Contexte technique', type: 'text', content: '', order: 1, visible: true, mandatory: true },
          { id: '2', title: 'Méthodologie OSINT', type: 'text', content: '', order: 2, visible: true, mandatory: true },
          { id: '3', title: 'Outils utilisés', type: 'table', content: '', order: 3, visible: true, mandatory: true },
          { id: '4', title: 'Données collectées', type: 'table', content: '', order: 4, visible: true, mandatory: true },
          { id: '5', title: 'Analyse technique', type: 'text', content: '', order: 5, visible: true, mandatory: true },
          { id: '6', title: 'Preuves digitales', type: 'text', content: '', order: 6, visible: true, mandatory: false },
          { id: '7', title: 'Recommandations techniques', type: 'text', content: '', order: 7, visible: true, mandatory: true }
        ],
        layout: {
          format: 'A4',
          orientation: 'portrait',
          margins: { top: 20, right: 20, bottom: 20, left: 20 },
          header: { enabled: true, height: 50, content: 'RAPPORT TECHNIQUE OSINT' },
          footer: { enabled: true, height: 30, content: 'Page {page} sur {total}' }
        },
        branding: {
          company_name: 'NetMeSafe',
          colors: { primary: '#FF5722', secondary: '#2D3748', accent: '#38B2AC' },
          fonts: { primary: 'Arial', secondary: 'Helvetica' }
        },
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        created_by: 'system'
      }
    ];

    try {
      const response = await apiService.get<ReportTemplate[]>(API_ENDPOINTS.REPORT_TEMPLATES);
      return response.data;
    } catch (error) {
      console.warn('API des templates indisponible, utilisation des templates par défaut');
      return fallbackTemplates;
    }
  }

  async createTemplate(template: Partial<ReportTemplate>): Promise<ReportTemplate> {
    const response = await apiService.post<ReportTemplate>(API_ENDPOINTS.REPORT_TEMPLATES, template);
    return response.data;
  }

  async updateTemplate(templateId: string, template: Partial<ReportTemplate>): Promise<ReportTemplate> {
    const response = await apiService.put<ReportTemplate>(`${API_ENDPOINTS.REPORT_TEMPLATES}/${templateId}`, template);
    return response.data;
  }

  async deleteTemplate(templateId: string): Promise<void> {
    await apiService.delete(`${API_ENDPOINTS.REPORT_TEMPLATES}/${templateId}`);
  }

  // Génération de rapports
  async generateReport(reportRequest: ReportRequest): Promise<ReportData> {
    const response = await apiService.post<ReportData>(API_ENDPOINTS.REPORT_GENERATE, reportRequest);
    return response.data;
  }

  // Gestion des rapports
  async getReports(params?: {
    page?: number;
    limit?: number;
    enquete_id?: string;
    status?: string;
  }): Promise<ReportData[]> {
    const queryParams = new URLSearchParams();
    
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.enquete_id) queryParams.append('enquete_id', params.enquete_id);
    if (params?.status) queryParams.append('status', params.status);

    const query = queryParams.toString();
    const url = `${API_ENDPOINTS.REPORTS}${query ? `?${query}` : ''}`;
    
    const response = await apiService.get<ReportData[]>(url);
    return response.data;
  }

  async getReportById(reportId: string): Promise<ReportData> {
    const response = await apiService.get<ReportData>(API_ENDPOINTS.REPORT_BY_ID(reportId));
    return response.data;
  }

  async updateReport(reportId: string, report: Partial<ReportData>): Promise<ReportData> {
    const response = await apiService.put<ReportData>(API_ENDPOINTS.REPORT_BY_ID(reportId), report);
    return response.data;
  }

  async deleteReport(reportId: string): Promise<void> {
    await apiService.delete(API_ENDPOINTS.REPORT_BY_ID(reportId));
  }

  async downloadReport(reportId: string): Promise<{ download_url: string; expires_in: number }> {
    const response = await apiService.get<{ download_url: string; expires_in: number }>(API_ENDPOINTS.REPORT_DOWNLOAD(reportId));
    return response.data;
  }

  // Analytics
  async getAnalytics(): Promise<ReportAnalytics> {
    const response = await apiService.get<ReportAnalytics>(`${API_ENDPOINTS.REPORTS}/analytics/dashboard`);
    return response.data;
  }

  // Utilitaires
  async previewReport(reportRequest: ReportRequest): Promise<{ preview_url: string }> {
    const response = await apiService.post<{ preview_url: string }>(`${API_ENDPOINTS.REPORTS}/preview`, reportRequest);
    return response.data;
  }

  async scheduleReport(reportRequest: ReportRequest & { schedule_date: string }): Promise<{ scheduled_id: string }> {
    const response = await apiService.post<{ scheduled_id: string }>(`${API_ENDPOINTS.REPORTS}/schedule`, reportRequest);
    return response.data;
  }

  async shareReport(reportId: string, recipients: string[], expires_in_hours: number = 24): Promise<{ share_url: string }> {
    const response = await apiService.post<{ share_url: string }>(`${API_ENDPOINTS.REPORTS}/${reportId}/share`, {
      recipients,
      expires_in_hours
    });
    return response.data;
  }

  // Validation
  async validateTemplate(template: ReportTemplate): Promise<{ valid: boolean; errors?: string[] }> {
    const response = await apiService.post<{ valid: boolean; errors?: string[] }>(`${API_ENDPOINTS.REPORT_TEMPLATES}/validate`, template);
    return response.data;
  }

  async estimateGenerationTime(reportRequest: ReportRequest): Promise<{ estimated_time_seconds: number }> {
    const response = await apiService.post<{ estimated_time_seconds: number }>(`${API_ENDPOINTS.REPORTS}/estimate`, reportRequest);
    return response.data;
  }
}

export const reportAPI = new ReportAPI();